import React, { useState, useEffect, useCallback } from "react";
import {
	TextField,
	MenuItem,
	Button,
	Typography,
	CircularProgress,
	Paper,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "../../contexts/useSnackBarContext";
import { useEnumTranslation } from "../../utils/translateEnum";
import {
	useSectorOptions,
	useCompanySizeOptions,
	useCompanyTypeOptions
} from "../../hooks/useCompany";
import { useCreateCompanyGroup } from "../../hooks/useCompanyGroup";
import { Company, CompanyCreateDTO, AddressCreateDTO } from "../../models/Company";
import { CompanyGroup } from "../../models/CompanyGroup";
import { Address as AddressDTO } from "../../models/Address";
import { zoullooColors } from "../../theme";
import PlacesAutocomplete from "../UI/PlacesAutocomplete";
import CompanyGroupAutocomplete from "../common/CompanyGroupAutocomplete";
import { useTranslatableForm } from "../../hooks/useTranslatableForm";
import { LanguageCode } from "../../models/enums/LanguageCode";
import TranslatableFieldWrapper from "../translation/TranslatableFieldWrapper";


export interface TranslatableFormData {
	primaryValues: Record<string, string>;
	modifiedLanguages: { languageCode: LanguageCode; fields: Record<string, string> }[];
}

interface CompanyFormProps {
	initialData?: Partial<Company & { sectorId?: number }>;
	initialAddress?: AddressDTO;
	initialCompanyGroup?: { id: number; name: string } | null;
	onSubmit: (data: CompanyCreateDTO, addressData?: AddressCreateDTO, translatableData?: TranslatableFormData) => void;
	isSubmitting: boolean;
	submitButtonText: string;
	isClientOnboarding?: boolean;
	companyId?: number;
}

const TRANSLATABLE_FIELDS = ["description", "market", "geographicPresence"];

const CompanyForm: React.FC<CompanyFormProps> = ({
	initialData = {},
	initialAddress,
	initialCompanyGroup,
	onSubmit,
	isSubmitting,
	submitButtonText,
	isClientOnboarding = false,
	companyId
}) => {
	const { t } = useTranslation();
	const { showError } = useSnackbarContext();
	const { translateCompanySize, translateCompanyType } = useEnumTranslation();


	// Data from API with loading states
	const { data: sectorOptions, isLoading: loadingSectors } = useSectorOptions();
	const { data: companySizeOptions, isLoading: loadingSizes } = useCompanySizeOptions();
	const { data: companyTypeOptions, isLoading: loadingTypes } = useCompanyTypeOptions();

	// State to track if options are loaded
	const [optionsLoaded, setOptionsLoaded] = useState(false);

	// Form state - with delayed initialization
	const [formData, setFormData] = useState<CompanyCreateDTO>({
		legalName: "",
		tradingName: "",
		sectorId: undefined,
		companySize: "",
		companyType: "",
		website: "",
		description: "",
		market: "",
		geographicPresence: ""
	});

	// Address state - with delayed initialization
	const [address, setAddress] = useState<AddressCreateDTO | null>(null);

	// Form validation state
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Company group state
	const [selectedCompanyGroup, setSelectedCompanyGroup] = useState<CompanyGroup | { id: number; name: string } | null>(null);
	const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
	const [newGroupName, setNewGroupName] = useState("");
	const [newGroupWebsite, setNewGroupWebsite] = useState("");
	const [createGroupError, setCreateGroupError] = useState("");
	const createGroupMutation = useCreateCompanyGroup();

	// Translatable form state. The translatable block is internal-only (hidden in client
	// onboarding), so the queries must be gated too: company translation reads are
	// ownership-protected server-side and would 403 for a client.
	const translatableForm = useTranslatableForm({
		entityType: "Company",
		entityId: companyId,
		translatableFields: TRANSLATABLE_FIELDS,
		initialValues: {
			description: initialData.description ?? "",
			market: initialData.market ?? "",
			geographicPresence: initialData.geographicPresence ?? "",
		},
		// A company is a SHARED entity (several internal users, each with their own personal
		// language, edit it): its authoring language is anchored to the platform primary (FR),
		// never to the editor's settings — otherwise the translation direction would depend on
		// who happens to edit. Passing it explicitly applies the same AI-first ruleset as talent
		// fields: the EN can only be personalised once generated, so it always remains
		// hand-backable (the FR human source necessarily exists).
		sourceLanguage: LanguageCode.Fr,
		enabled: !isClientOnboarding,
	});

	// Shared TranslatableFieldWrapper props for one translatable field: status chip state and the
	// explicit unlock (draft) flow. No formik side-channel here — the submit reads the hook's
	// committed values, so a cancelled draft simply never leaves the wrapper.
	const translatableWrapperProps = (field: string) => ({
		editingLanguage: translatableForm.editingLanguage,
		onLanguageChange: translatableForm.switchLanguage,
		isStale: translatableForm.isFieldStale(field),
		uiState: translatableForm.getFieldUiState(field),
		sourceLanguage: translatableForm.sourceLanguage,
		showLanguageSelector: translatableForm.isLanguageSelectorVisible(field),
		showAiFirstNote: translatableForm.isAiFirstNoteVisible(field),
		isDraft: translatableForm.isFieldDraft(field),
		onBeginDraft: () => translatableForm.beginFieldDraft(field),
		onCancelDraft: () => translatableForm.cancelFieldDraft(field),
		canHandBackToAi: translatableForm.isFieldHandBackable(field),
		isRegenerating: translatableForm.isFieldRegenerating(field),
		onEnableAi: () => translatableForm.enableAiForField(field),
		contentReadyToken: translatableForm.getFieldContentToken(field),
	});

	// Track if form is ready for submission
	const isLoading = loadingSectors || loadingSizes || loadingTypes;

	// Important: First check if all options are loaded
	useEffect(() => {
		if (!isLoading && sectorOptions && companySizeOptions && companyTypeOptions) {
			setOptionsLoaded(true);
		}
	}, [isLoading, sectorOptions, companySizeOptions, companyTypeOptions]);

	// ONLY initialize form and address data AFTER options are loaded
	useEffect(() => {
		// Only when options are loaded, set the initial data
		if (optionsLoaded && initialData) {
			setFormData(prev => ({
				...prev,
				...initialData
			}));

			// And if we have address data, set that too
			if (initialAddress) {
				setAddress({
					fullAddress: initialAddress.fullAddress,
					street: initialAddress.street ?? "",
					streetNumber: initialAddress.streetNumber ?? "",
					postalCode: initialAddress.postalCode ?? "",
					city: initialAddress.city,
					region: initialAddress.region ?? "",
					country: initialAddress.country,
					countryCode: initialAddress.countryCode ?? "",
					latitude: initialAddress.latitude,
					longitude: initialAddress.longitude,
					placeId: initialAddress.placeId ?? ""
				});
			}

			// Initialize company group if provided
			if (initialCompanyGroup) {
				setSelectedCompanyGroup(initialCompanyGroup);
			}
		}
	}, [optionsLoaded, initialData, initialAddress, initialCompanyGroup]);


	// Format website URL - add https:// if no protocol is provided
	const formatWebsiteUrl = (url: string): string => {
		if (!url) return url;
		
		// If URL doesn't start with http:// or https://, add https://
		if (!url.match(/^https?:\/\//)) {
			return `https://${url}`;
		}
		
		return url;
	};

	// Handle form input changes
	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		
		// Special handling for website field
		if (name === 'website') {
			// Don't format while typing, only on blur
			setFormData(prev => ({ ...prev, [name]: value }));
		} else {
			setFormData(prev => ({ ...prev, [name]: value }));
		}

		// Clear error when field is edited
		if (errors[name]) {
			setErrors(prev => ({ ...prev, [name]: "" }));
		}
	};

	// Handle website field blur to format URL
	const handleWebsiteBlur = () => {
		if (formData.website) {
			const formattedUrl = formatWebsiteUrl(formData.website);
			setFormData(prev => ({ ...prev, website: formattedUrl }));
		}
	};

	// Validate basic company information
	const validateBasicInfo = (errors: Record<string, string>) => {
		if (!formData.legalName) {
			errors.legalName = t("validation.required");
		}
		
		// Always require sector and company size
		if (!formData.sectorId) {
			errors.sectorId = t("validation.required");
		}
		
		if (!formData.companySize) {
			errors.companySize = t("validation.required");
		}
	};

	// Validate fields required for internal users only
	const validateInternalFields = (errors: Record<string, string>) => {
		if (!formData.companyType) {
			errors.companyType = t("validation.required");
		}
	};

	// Validate website with multiple rules
	const validateWebsite = (errors: Record<string, string>) => {
		if (!formData.website) {
			errors.website = t("validation.required");
			return;
		}

		// Format the URL for validation (add https:// if missing)
		const formattedUrl = formatWebsiteUrl(formData.website);
		
		// Validate the formatted URL
		if (
			!/^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,})([/\w .-]+)*\/?$/.test(formattedUrl)
		) {
			errors.website = t("validation.invalidUrl");
		}
	};

	// Validate address fields - address is now always required
	const validateAddress = (errors: Record<string, string>) => {
		// Address is required, so check if it has been set
		if (!address || !address.fullAddress) {
			errors.fullAddress = t("validation.required");
			return;
		}

		// If address exists, validate required fields
		if (!address.city) {
			errors.city = t("validation.required");
		}
		if (!address.country) {
			errors.country = t("validation.required");
		}
	};

	// Focus on first error field
	const focusOnFirstError = (errors: Record<string, string>) => {
		const firstErrorField = Object.keys(errors)[0];
		const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
		if (errorElement) {
			(errorElement as HTMLElement).focus();
		}
	};

	// Main form validation function
	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		validateBasicInfo(newErrors);

		if (!isClientOnboarding) {
			validateInternalFields(newErrors);
		}

		validateWebsite(newErrors);
		validateAddress(newErrors);

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0) {
			focusOnFirstError(newErrors);
			// Some required fields (city/country from the address) have NO visible error slot, so a
			// failed validation used to look like a dead Save button ("clic dans le vide"). Always
			// surface a snackbar so the user gets feedback and knows the save was blocked.
			showError("company.validation.missingRequiredFields");
			return false;
		}

		return true;
	};

	// Handle inline group creation
	const handleCreateGroup = async () => {
		if (!newGroupName.trim()) {
			setCreateGroupError(t("companyGroup.nameRequired"));
			return;
		}

		try {
			const createdGroup = await createGroupMutation.mutateAsync({
				name: newGroupName.trim(),
				website: newGroupWebsite.trim() || undefined
			});
			setSelectedCompanyGroup(createdGroup);
			setShowCreateGroupDialog(false);
			setNewGroupName("");
			setNewGroupWebsite("");
			setCreateGroupError("");
		} catch {
			setCreateGroupError(t("companyGroup.creationError"));
		}
	};

	// Handle form submission
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Format website URL before validation and submission
		if (formData.website) {
			const formattedUrl = formatWebsiteUrl(formData.website);
			setFormData(prev => ({ ...prev, website: formattedUrl }));
			formData.website = formattedUrl; // Update local reference for validation
		}

		if (validateForm()) {
			// Include companyGroupId in submitted data
			// Use primary language values for translatable fields
			const primaryValues = translatableForm.getPrimaryValues();
			const submitData = {
				...formData,
				...primaryValues,
				companyGroupId: selectedCompanyGroup?.id ?? undefined
			};
			// Always submit with address data since it's now required
			onSubmit(submitData, address || undefined, {
				primaryValues,
				modifiedLanguages: translatableForm.getModifiedLanguages(),
			});
		}
	};

	// Convert Google Places result to AddressCreateDTO 
	const convertPlaceResultToAddressCreateDTO = useCallback(
		(placeResult: google.maps.places.PlaceResult): AddressCreateDTO => {
			const addressComponents = placeResult.address_components || [];
			const getComponent = (type: string) =>
				addressComponents.find(component => component.types.includes(type))?.long_name ||
				"";

			return {
				fullAddress: placeResult.formatted_address || "",
				street: getComponent("route"),
				streetNumber: getComponent("street_number"),
				postalCode: getComponent("postal_code"),
				city: getComponent("locality") || getComponent("administrative_area_level_2"),
				region: getComponent("administrative_area_level_1"),
				country: getComponent("country"),
				countryCode: addressComponents.find(component =>
					component.types.includes("country")
				)?.short_name || "",
				latitude: placeResult.geometry?.location?.lat(),
				longitude: placeResult.geometry?.location?.lng(),
				placeId: placeResult.place_id || "",
				formattedAddress: placeResult.formatted_address
			};
		},
		[]
	);

	// Handle address changes from PlacesAutocomplete
	const handleAddressChange = useCallback(
		(place: google.maps.places.PlaceResult | null) => {
			if (place) {
				const addressData = convertPlaceResultToAddressCreateDTO(place);
				setAddress(addressData);
			} else {
				setAddress(null);
			}

			// Clear address errors
			if (errors.fullAddress) {
				setErrors(prev => ({ ...prev, fullAddress: "", city: "", country: "" }));
			}
		},
		[errors.fullAddress, convertPlaceResultToAddressCreateDTO]
	);

	// Show loading state while options are being loaded
	if (!optionsLoaded) {
		return (
			<Paper
				elevation={3}
				sx={{
					p: 3,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 200
				}}
			>
				<CircularProgress />
				<Typography variant="body1" sx={{ ml: 2 }}>
					{t("common.loading")}
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper elevation={0} sx={{ p: 4 }}>
			<form onSubmit={handleSubmit} noValidate>
				<Grid container spacing={3}>
					<Grid size={12}>
						<Typography variant="h2" component="h1" sx={{ mb: 3, fontWeight: "bold" }}>
							{t("company.companyInformation")}
						</Typography>
					</Grid>

					{/* Basic Information */}
					<Grid size={{ xs: 12, md: 6 }}>
						<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
							{t("company.legalName")} *
						</Typography>
						<TextField
							fullWidth
							name="legalName"
							value={formData.legalName}
							onChange={handleChange}
							error={!!errors.legalName}
							helperText={errors.legalName}
							required
						/>
					</Grid>

					{!isClientOnboarding && (
						<Grid size={{ xs: 12, md: 6 }}>
							<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
								{t("company.tradingName")}
							</Typography>
							<TextField
								fullWidth
								name="tradingName"
								value={formData.tradingName}
								onChange={handleChange}
								// Trading name is the only optional field
							/>
						</Grid>
					)}

					{isClientOnboarding ? (
						<>
							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.industry")} *
								</Typography>
								<TextField
									fullWidth
									select
									id="sector-select"
									name="sectorId"
									value={formData.sectorId ?? ""}
									onChange={handleChange}
									error={!!errors.sectorId}
									helperText={errors.sectorId}
									required
								>
									<MenuItem value="">{t("common.select")}</MenuItem>
									{sectorOptions?.map(option => (
										<MenuItem key={option.id} value={option.id}>
											{t(`Sectors.${option.label}`)}
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.size")} *
								</Typography>
								<TextField
									fullWidth
									select
									id="company-size-select"
									name="companySize"
									value={formData.companySize ?? ""}
									onChange={handleChange}
									error={!!errors.companySize}
									helperText={errors.companySize}
									required
								>
									<MenuItem value="">{t("common.select")}</MenuItem>
									{companySizeOptions?.map(option => (
										<MenuItem key={option} value={option}>
											{translateCompanySize(option)}
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.website")} *
								</Typography>
								<TextField
									fullWidth
									name="website"
									value={formData.website}
									onChange={handleChange}
									onBlur={handleWebsiteBlur}
									error={!!errors.website}
									helperText={errors.website || t("validation.websiteHelp")}
									placeholder="example.com ou https://example.com"
								/>
							</Grid>
						</>
					) : (
						<>
							<Grid size={{ xs: 12, md: 4 }}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.industry")} *
								</Typography>
								<TextField
									fullWidth
									select
									id="sector-select"
									name="sectorId"
									value={formData.sectorId ?? ""}
									onChange={handleChange}
									error={!!errors.sectorId}
									helperText={errors.sectorId}
									required
								>
									<MenuItem value="">{t("common.select")}</MenuItem>
									{sectorOptions?.map(option => (
										<MenuItem key={option.id} value={option.id}>
											{t(`Sectors.${option.label}`)}
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid size={{ xs: 12, md: 4 }}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.size")} *
								</Typography>
								<TextField
									fullWidth
									select
									id="company-size-select"
									name="companySize"
									value={formData.companySize ?? ""}
									onChange={handleChange}
									error={!!errors.companySize}
									helperText={errors.companySize}
									required
								>
									<MenuItem value="">{t("common.select")}</MenuItem>
									{companySizeOptions?.map(option => (
										<MenuItem key={option} value={option}>
											{translateCompanySize(option)}
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid size={{ xs: 12, md: 4 }}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.type")} *
								</Typography>
								<TextField
									fullWidth
									select
									id="company-type-select"
									name="companyType"
									value={formData.companyType ?? ""}
									onChange={handleChange}
									error={!!errors.companyType}
									helperText={errors.companyType}
									required
								>
									<MenuItem value="">{t("common.select")}</MenuItem>
									{companyTypeOptions?.map(option => (
										<MenuItem key={option} value={option}>
											{translateCompanyType(option)}
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid size={12}>
								<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
									{t("company.website")} *
								</Typography>
								<TextField
									fullWidth
									name="website"
									value={formData.website}
									onChange={handleChange}
									onBlur={handleWebsiteBlur}
									error={!!errors.website}
									helperText={errors.website || t("validation.websiteHelp")}
									placeholder="example.com ou https://example.com"
									required
								/>
							</Grid>
						</>
					)}

					{/* Headquarters Address */}
					<Grid size={12}>
						<Typography
							variant="h5"
							sx={{ mt: 4, mb: 2, fontWeight: 600 }}
						>
							{t("company.headquartersAddress")} *
						</Typography>
					</Grid>

					<Grid size={12}>
						<PlacesAutocomplete
							name="fullAddress"
							value={
								address
									? ({
											place_id: address.placeId,
											formatted_address: address.fullAddress
									  } as google.maps.places.PlaceResult)
									: null
							}
							onChange={handleAddressChange}
							onBlur={() => {
								// Handle blur if needed for validation
							}}
							types={["address"]}
							componentRestrictions={{ country: [] }}
							fields={[
								"address_components",
								"formatted_address",
								"geometry",
								"place_id"
							]}
							minInputLength={3}
							label=""
							placeholder={t("company.addressPlaceholder")}
							variant="outlined"
							fullWidth
							error={!!errors.fullAddress}
							helperText={errors.fullAddress}
						/>
					</Grid>

					{/* Company Group - only show for internal users */}
					{!isClientOnboarding && (
						<>
							<Grid size={12}>
								<Typography
									variant="h5"
									sx={{ mt: 4, mb: 2, fontWeight: 600 }}
								>
									{t("company.group")}
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<CompanyGroupAutocomplete
									value={selectedCompanyGroup}
									onChange={(group) => setSelectedCompanyGroup(group)}
									showCreateButton
									onCreateNew={() => setShowCreateGroupDialog(true)}
								/>
							</Grid>
						</>
					)}

					{/* Translatable fields - only show for internal users */}
					{!isClientOnboarding && (
						<>
							<Grid size={12}>
								<Typography
									variant="h5"
									sx={{ mt: 4, mb: 2, fontWeight: 600 }}
								>
									{t("company.additionalInformation")}
								</Typography>
							</Grid>

							<Grid size={12}>
								<TranslatableFieldWrapper
									label={t("company.description")}
									{...translatableWrapperProps("description")}
								>
									<TextField
										fullWidth
										multiline
										rows={4}
										name="description"
										// Native read-only carries the AI lock (the wrapper only dims): the text
										// stays scrollable/selectable/copiable, unlike the old pointer-events tarp.
										InputProps={{ readOnly: translatableForm.isFieldReadOnly("description") }}
										value={translatableForm.getFieldValue("description")}
										onChange={(e) => translatableForm.setFieldValue("description", e.target.value)}
										placeholder={t("company.descriptionPlaceholder")}
										error={!!errors.description}
									/>
								</TranslatableFieldWrapper>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<TranslatableFieldWrapper
									label={t("company.market")}
									{...translatableWrapperProps("market")}
								>
									<TextField
										fullWidth
										multiline
										rows={3}
										name="market"
										InputProps={{ readOnly: translatableForm.isFieldReadOnly("market") }}
										value={translatableForm.getFieldValue("market")}
										onChange={(e) => translatableForm.setFieldValue("market", e.target.value)}
										placeholder={t("company.marketPlaceholder")}
										error={!!errors.market}
									/>
								</TranslatableFieldWrapper>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<TranslatableFieldWrapper
									label={t("company.geographicPresence")}
									{...translatableWrapperProps("geographicPresence")}
								>
									<TextField
										fullWidth
										multiline
										rows={3}
										name="geographicPresence"
										InputProps={{ readOnly: translatableForm.isFieldReadOnly("geographicPresence") }}
										value={translatableForm.getFieldValue("geographicPresence")}
										onChange={(e) => translatableForm.setFieldValue("geographicPresence", e.target.value)}
										placeholder={t("company.geographicPresencePlaceholder")}
										error={!!errors.geographicPresence}
									/>
								</TranslatableFieldWrapper>
							</Grid>
						</>
					)}

					{/* Submit button */}
					<Grid size={12} sx={{ mt: 2 }}>
						<Button
							type="submit"
							variant="contained"
							size="large"
							disabled={isSubmitting}
							sx={{
								backgroundColor: zoullooColors.secondary.main,
								color: zoullooColors.secondary.contrastText,
								"&:hover": {
									backgroundColor: zoullooColors.secondary.dark
								},
								px: 4,
								py: 1.5
							}}
						>
							{isSubmitting ? (
								<CircularProgress size={24} color="inherit" />
							) : (
								submitButtonText
							)}
						</Button>
					</Grid>
				</Grid>
			</form>

			{/* Create Group Dialog */}
			<Dialog
				open={showCreateGroupDialog}
				onClose={() => {
					setShowCreateGroupDialog(false);
					setNewGroupName("");
					setNewGroupWebsite("");
					setCreateGroupError("");
				}}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("company.createGroupDialogTitle")}</DialogTitle>
				<DialogContent>
					{createGroupError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{createGroupError}
						</Alert>
					)}
					<TextField
						fullWidth
						label={t("company.groupName")}
						value={newGroupName}
						onChange={(e) => {
							setNewGroupName(e.target.value);
							if (createGroupError) setCreateGroupError("");
						}}
						required
						sx={{ mt: 1, mb: 2 }}
					/>
					<TextField
						fullWidth
						label={t("company.groupWebsite")}
						value={newGroupWebsite}
						onChange={(e) => setNewGroupWebsite(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setShowCreateGroupDialog(false);
							setNewGroupName("");
							setNewGroupWebsite("");
							setCreateGroupError("");
						}}
					>
						{t("common.cancel")}
					</Button>
					<Button
						onClick={handleCreateGroup}
						variant="contained"
						disabled={createGroupMutation.isPending}
						sx={{
							backgroundColor: zoullooColors.secondary.main,
							"&:hover": {
								backgroundColor: zoullooColors.secondary.dark
							}
						}}
					>
						{createGroupMutation.isPending ? (
							<CircularProgress size={24} color="inherit" />
						) : (
							t("companyGroup.create")
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
};

export default CompanyForm;
